/**
 * Gutter Calculator
 * File: assets/gutter-calculator-new.js
 *
 * Reads from window.gutterCalcDataAluflow / window.gutterCalcDataQuadraflo
 * pre-filled by snippets/gutter-calculator.liquid
 *
 * HTML attribute mapping (existing template):
 *   Shape tiles:    .roofimg  title="roof_name"  data-roof-key="roof_key"
 *   Quality select: select[name="tag2"]  — "Aluflow Gutter Calc" | "Quadraflo Gutter Calc"
 *   Colour selects: select[name="colour1"]  .aclr (aluflow) / .qclr (quadraflo)
 *   Storey select:  select[name="storey"]   — "Single storey" | "Double storey"
 *   Size inputs:    input[name="lengtha/b/c"]
 *   Next (step 2→3): .rfsize
 *   Calculate:      .ckit
 *   Back to step 1: .srtype
 *   Kit list:       .kit-list (written by JS)
 *   Add to cart:    .kit-add-to-cart (written by JS)
 */

(function () {
  'use strict';

  // ─── Guard ───────────────────────────────────────────────────────────────

  if (!window.gutterCalcDataAluflow && !window.gutterCalcDataQuadraflo) {
    console.warn('Gutter calculator: no metaobject data. Check snippet and metaobject handles.');
    return;
  }

  console.log('Gutter calculator ready.');

  // Active dataset — swapped when customer selects quality
  var data = null;

  // ─── State ───────────────────────────────────────────────────────────────

  var state = {
    roofKey:    '',
    roofName:   '',
    colour:     '',
    storey:     '',   // 'single' or 'double'
    sizeA:      0,
    sizeB:      0,
    sizeC:      0,
    cartItems:  [],
    resolvedQtys: {}  // keyed by entryLabel — for cross-product formulas
  };


  // ─── Helpers ─────────────────────────────────────────────────────────────

  // Colour name translation map — normalises French variant title colour words
  // to English equivalents for matching against English colour select values.
  var colourMap = {
    'noir':       'black',
    'blanc':      'white',
    'gris':       'grey',
    'grisfonce':  'darkgrey',
    'couleurral': 'ralcolour',
    'ralcolour':  'ralcolour'
  };

  function normaliseTitle(str) {
    var norm = str.replace(/\s/g, '').replace(/,/g, '.').replace(/-/g, '').toLowerCase();
    for (var fr in colourMap) {
      if (norm.indexOf(fr) === 0) {
        norm = norm.replace(fr, colourMap[fr]);
        break;
      }
    }
    return norm;
  }

  function findVariantByLabel(product, label) {
    if (!product || !product.variants) return null;
    var norm = label.replace(/\s/g, '').replace(/,/g, '.').replace(/-/g, '').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var t = normaliseTitle(product.variants[i].title);
      if (t.indexOf(norm) !== -1) return product.variants[i];
    }
    return null;
  }

  // Match by colour + length label. Falls back to length-only when colour
  // match fails (handles French locale where variant titles are translated
  // e.g. "Noir / 2000" but colour select value is "Black").
  // When lengthLabel is empty, matches by colour only, falls back to variants[0].
  function findColouredVariant(product, lengthLabel, colour) {
    if (!product || !product.variants) return null;
    var normLen = lengthLabel.replace(/\s/g, '').replace(/,/g, '.').replace(/-/g, '').toLowerCase();
    var normCol = colour.replace(/\s/g, '').replace(/-/g, '').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = normaliseTitle(product.variants[i].title);
      if (normLen === '') {
        if (normTitle.indexOf(normCol) !== -1) return product.variants[i];
      } else {
        if (normTitle.indexOf(normLen) !== -1 && normTitle.indexOf(normCol) !== -1) return product.variants[i];
      }
    }
    // Fallback: length-only match (handles translated colour names on FR)
    if (normLen !== '') return findVariantByLabel(product, lengthLabel);
    return product.variants[0];
  }

  function getProduct(rule) {
    return (rule.products && rule.products.length > 0) ? rule.products[0] : null;
  }

  function totalRun() {
    return state.sizeA + (state.sizeB || 0) + (state.sizeC || 0);
  }

  function getOverride(rule) {
    var ov = rule.roofTypeOverrides;
    if (typeof ov === 'string') { try { ov = JSON.parse(ov); } catch(e) { return null; } }
    if (!ov || Object.keys(ov).length === 0) return {};
    return ov[state.roofKey] !== undefined ? ov[state.roofKey] : null;
  }

  function getStoreyOverride(ov) {
    if (!ov) return null;
    if (ov.single || ov.double) return ov[state.storey] || null;
    return ov;
  }

  function storeyApplies(rule) {
    var sa = rule.storeyApplicability || 'always';
    if (sa === 'always') return true;
    if (sa === 'single_only' && state.storey === 'single') return true;
    if (sa === 'double_only' && state.storey === 'double') return true;
    return false;
  }

  function formatMoney(pence) {
    var cur = (data && data.currency) ? data.currency : {};
    var locale = cur.locale || 'en-GB';
    var code   = cur.code   || 'GBP';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(pence);
    } catch(e) {
      return (cur.symbol || '£') + pence.toFixed(2);
    }
  }


  // ─── Aluflow bin-pack ────────────────────────────────────────────────────
  // Stock: 2000 / 3000 / 4000mm
  // Priority: fewest pieces → fewest unique variants → lowest wastage

  function aluflowBinPack(total) {
    if      (total <= 2000)  return [{l:'2000mm',q:1}];
    else if (total <= 3000)  return [{l:'3000mm',q:1}];
    else if (total <= 4000)  return [{l:'4000mm',q:1}];
    else if (total <= 5000)  return [{l:'2000mm',q:1},{l:'3000mm',q:1}];
    else if (total <= 6000)  return [{l:'3000mm',q:2}];
    else if (total <= 7000)  return [{l:'3000mm',q:1},{l:'4000mm',q:1}];
    else if (total <= 8000)  return [{l:'4000mm',q:2}];
    else if (total <= 9000)  return [{l:'3000mm',q:3}];
    else if (total <= 10000) return [{l:'3000mm',q:2},{l:'4000mm',q:1}];
    else if (total <= 11000) return [{l:'3000mm',q:1},{l:'4000mm',q:2}];
    else if (total <= 12000) return [{l:'4000mm',q:3}];
    else {
      var splits = [];
      var rem = total;
      while (rem > 12000) { splits.push({l:'4000mm',q:1}); splits.push({l:'3000mm',q:1}); rem -= 7000; }
      return splits.concat(aluflowBinPack(rem));
    }
  }

  function aluflowGutterLengthSplit(product) {
    var splits = aluflowBinPack(totalRun());
    var cons = {};
    for (var i = 0; i < splits.length; i++) {
      cons[splits[i].l] = (cons[splits[i].l] || 0) + splits[i].q;
    }
    var pieces = 0;
    for (var lbl in cons) {
      var v = findColouredVariant(product, lbl, state.colour);
      if (v) {
        pieces += cons[lbl];
        state.cartItems.push({ product: product, variant: v, quantity: cons[lbl], editable: true });
      }
    }
    return pieces;
  }


  // ─── Core calculation ─────────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems   = [];
    state.resolvedQtys = {};

    var rules = data.productRules;

    for (var r = 0; r < rules.length; r++) {
      var rule    = rules[r];
      var product = getProduct(rule);
      if (!product) continue;
      if (!storeyApplies(rule)) continue;

      var formula         = rule.qtyFormula;
      var variantDrivenBy = rule.variantDrivenBy;
      var quantity = 0;
      var variant  = null;

      if (formula === 'aluflow_gutter_length_split') {
        var pieces = aluflowGutterLengthSplit(product);
        state.resolvedQtys[rule.entryLabel] = pieces;
        continue;
      }

      if (variantDrivenBy === 'colour_hand_pair') {
        var lh = findVariantByLabel(product, state.colour + ' / LH');
        var rh = findVariantByLabel(product, state.colour + ' / RH');
        if (lh) state.cartItems.push({ product: product, variant: lh, quantity: 1, editable: true });
        if (rh) state.cartItems.push({ product: product, variant: rh, quantity: 1, editable: true });
        state.resolvedQtys[rule.entryLabel] = 2;
        continue;
      }

      var rawOv    = getOverride(rule);
      var override = getStoreyOverride(rawOv);
      if (override === null) continue;

      if (variantDrivenBy === 'colour') {
        variant = override.variant_label
          ? findColouredVariant(product, override.variant_label, state.colour)
          : findColouredVariant(product, '', state.colour);
      }

      if (!variant) continue;

      if (formula === 'fixed') {
        quantity = override.fixed_qty !== undefined ? override.fixed_qty : (rule.qtyMultiplierDefault || 1);
      } else if (formula === 'ceil_total_abc_div_4000') {
        quantity = Math.ceil(totalRun() / 4000) * (override.multiplier || 1);
      } else if (formula === 'ceil_total_abc_div_600') {
        quantity = Math.ceil(totalRun() / 600) * (override.multiplier || 1);
      } else if (formula === 'aluflow_union') {
        var p1 = state.resolvedQtys['Aluflow - Aluminium Gutter']      || 0;
        var p4 = state.resolvedQtys['Aluflow - Running Outlet']         || 0;
        var p6 = state.resolvedQtys['Aluflow - 90 Deg Internal Angle']  || 0;
        var p7 = state.resolvedQtys['Aluflow - 90 Deg External Angle']  || 0;
        quantity = Math.max(0, ((p4 + p6 + p7) * 2) + p1 - 1);
      } else if (formula === 'quadraflo_union') {
        quantity = Math.max(0, (state.resolvedQtys['Quadraflo - Deep Flow Gutter'] || 0) - 1);
      }

      state.resolvedQtys[rule.entryLabel] = (state.resolvedQtys[rule.entryLabel] || 0) + quantity;

      if (quantity > 0) {
        state.cartItems.push({ product: product, variant: variant, quantity: quantity, editable: true });
      }
    }

    renderKitList();
  }


  // ─── Kit list rendering ───────────────────────────────────────────────────

  function renderKitList() {
    var containers = document.querySelectorAll('.kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!containers.length) { console.warn('Gutter calculator: .kit-list not found'); return; }

    var totalPrice = 0;
    var totalSaved = 0;
    var html  = '';
    var mhtml = '';

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (!item.variant) continue;

      var price     = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      totalPrice += lineTotal;
      if (compareAt) totalSaved += (compareAt - price) * item.quantity;

      // Prefer variant-specific image, fall back to first product image
      var img = item.variant.image || (item.product.images && item.product.images[0]) ? (item.variant.image || item.product.images[0]) : '';

      // Desktop row
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '<div class="kit-item-image">' + (img ? '<img src="' + img + '" alt="' + item.product.title + '">' : '') + '</div>';
      html += '<div class="kit-item-details">';
      html += '<div class="kit-item-title">' + item.product.title + '</div>';
      html += '<div>' + item.variant.title + '</div>';
      html += '<div>SKU: ' + (item.variant.sku || '—') + '</div>';
      if (compareAt) {
        html += '<div><span class="kit-price-sale">' + formatMoney(price) + '</span><span class="kit-price-compare">' + formatMoney(compareAt) + '</span></div>';
      } else {
        html += '<div>' + formatMoney(price) + '</div>';
      }
      html += '</div>';
      html += '<div class="kit-item-qty">';
      html += '<button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '<input class="kit-qty-input" type="number" min="0" value="' + item.quantity + '" data-index="' + i + '">';
      html += '<button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '</div>';
      html += '<div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile row
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '<div class="w-100"><div class="w-25"><div class="kit-item-image">';
      if (img) mhtml += '<img src="' + img + '" alt="' + item.product.title + '">';
      mhtml += '</div></div><div class="w-75"><div class="kit-item-details">';
      mhtml += '<div class="kit-item-title">' + item.product.title + '</div>';
      mhtml += '<div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '<div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '<span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '<span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '<span>' + formatMoney(price) + '</span>';
      }
      mhtml += '</div></div>';
      mhtml += '<div class="kit-item-qty">';
      mhtml += '<button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '<input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '<button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '</div></div></div></div>';
    }

    containers.forEach(function(c) { c.innerHTML = html; });
    if (mcontainer) mcontainer.innerHTML = mhtml;

    $('.kitcart .total span').text(formatMoney(totalPrice));
    if (totalSaved > 0) {
      $('.saved').show();
      $('.kit-saved-amount').text(formatMoney(totalSaved));
    } else {
      $('.saved').hide();
    }

    $('.rooftype, .roofsheet, .roofsize').hide();
    $('.kit-list-wrapper').show();

    $(".roofcal .number").removeClass("current complete");
    $(".roofcal .number.wtor, .roofcal .number.yrc, .roofcal .number.cyrs").addClass("complete");
    $(".roofcal .number.kit").addClass("current");

    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) { state.cartItems[idx].quantity--; renderKitList(); }
      });
    });
    document.querySelectorAll('.kit-qty-plus').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-qty-input').forEach(function(inp) {
      inp.addEventListener('change', function() {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-add-to-cart').forEach(function(btn) {
      btn.addEventListener('click', function() {
        document.querySelectorAll('.kit-add-to-cart').forEach(function(b) {
          b.setAttribute('disabled', 'disabled');
          b.textContent = 'Adding to cart...';
        });
        addToCart();
      });
    });
  }


  // ─── Cart submission ──────────────────────────────────────────────────────

  function addToCart() {
    var items = [];
    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity:   item.quantity,
        id:         item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR':    state.colour,
          '_STOREY':    state.storey,
          '_LENGTH A':  state.sizeA,
          '_LENGTH B':  state.sizeB,
          '_LENGTH C':  state.sizeC
        }
      });
    }

    if (!items.length) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function(b) {
        b.removeAttribute('disabled'); b.textContent = 'Add full kit to cart';
      });
      return;
    }

    items.reverse();

    jQuery.post('/cart/add.js', { items: items })
      .then(function() {
        window.location.href = window.location.pathname.indexOf('fr-fr') === 1 ? '/fr-fr/cart' : '/cart';
      })
      .fail(function() {
        alert('There was an error adding to cart. Please try again.');
        document.querySelectorAll('.kit-add-to-cart').forEach(function(b) {
          b.removeAttribute('disabled'); b.textContent = 'Add full kit to cart';
        });
      });
  }


  // ─── Size input visibility ────────────────────────────────────────────────

  function updateSizeInputs() {
    $('input[name="lengthb"]').parent().show();
    $('input[name="lengthc"]').parent().show();
    if (state.roofKey === 'one_straight_side') {
      $('input[name="lengthb"]').parent().hide();
      $('input[name="lengthc"]').parent().hide();
      state.sizeB = 0; state.sizeC = 0;
    } else if (state.roofKey === 'two_sides_one_ext_90' || state.roofKey === 'two_sides_one_int_90') {
      $('input[name="lengthc"]').parent().hide();
      state.sizeC = 0;
    }
  }


  // ─── Diagram image ────────────────────────────────────────────────────────

  function updateDiagram() {
    if (!state.roofKey || !state.colour || !data) return;
    var brand  = (data.gutterBrand || 'aluflow').toLowerCase();
    var colour = state.colour.toLowerCase().replace(/\s+/g, '-');
    if (colour == 'ral-colour') { colour = 'ral'; }
    var file   = brand + '-gutter-' + state.roofKey.replace(/_/g, '-') + '-' + colour + '.webp';
    $('.roofsheet img, .roofsize img, .qimg img').attr('src', '/cdn/shop/files/' + file);
  }


  // ─── Step management ─────────────────────────────────────────────────────

  function setStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    $(".roofcal .number").removeClass("current complete");
    if (step === 1) {
      $('.rooftype').show();
      $(".roofcal .number.wtor").addClass("current");
    } else if (step === 2) {
      $('.roofsheet').show();
      $(".roofcal .number.wtor").addClass("complete");
      $(".roofcal .number.yrc").addClass("current");
    } else if (step === 3) {
      $('.roofsize').show();
      $(".roofcal .number.wtor, .roofcal .number.yrc").addClass("complete");
      $(".roofcal .number.cyrs").addClass("current");
    }
  }


  // ─── Quality/data swap helper ─────────────────────────────────────────────

  function applyQuality(q) {
    if (q === 'Aluflow Gutter Calc') {
      data = window.gutterCalcDataAluflow;
      $('.aclr').show(); $('.qclr').hide();
    } else if (q === 'Quadraflo Gutter Calc') {
      data = window.gutterCalcDataQuadraflo;
      $('.qclr').show(); $('.aclr').hide();
    } else {
      data = null;
      $('.aclr, .qclr').hide();
    }
    var sel = document.querySelector('select[name="tag2"]');
    if (sel) {
      var opt = sel.querySelector('option[value="' + q + '"]');
      if (opt) $(".gquote").text(opt.getAttribute('key') || '');
    }
  }


  // ─── Event bindings ───────────────────────────────────────────────────────

  $(".roofcal .roofimg").click(function() {
    var key  = $(this).attr('data-roof-key');
    var name = $(this).attr('title');
    if (!key) { console.warn('Gutter calculator: roofimg missing data-roof-key'); return; }
    state.roofKey  = key;
    state.roofName = name;
    state.colour   = '';
    state.storey   = '';
    $('select[name="tag2"]').val('');
    $('select[name="colour1"]').val('');
    $('select[name="storey"]').val('');

    var baseUrl = $(this).find('img').attr('src');
    $('.roofsheet img').attr('key', baseUrl);
    $('.roofsheet img').attr('src', baseUrl);
    $('.roofsize img').attr('src', baseUrl);
    $('.qimg img').attr('src', baseUrl);

    setStep(2);
  });

  $(".roofcal .number.wtor.srtype").click(function() {
    state.roofKey = ''; state.roofName = '';
    setStep(1);
  });

  $('.roofcal .number.yrc').click(function() {
    setStep(2);
  });

  $('select[name="tag2"]').change(function() {
    state.colour = '';
    $('select[name="colour1"]').val('');
    applyQuality($(this).val());
  });

  $('select[name="colour1"]').change(function() {
    state.colour = $(this).val();
    $('.quote .clr').text(this.options[this.selectedIndex].text);
    updateDiagram();
  });

  $('select[name="storey"]').change(function() {
    var v = $(this).val().toLowerCase();
    state.storey = v.indexOf('double') !== -1 ? 'double' : 'single';
    $('.story').text(this.options[this.selectedIndex].text);
  });

  $('.rfsize').click(function() {
    if (!state.roofKey) { alert('Please select a gutter shape'); return false; }
    if (!state.colour)  { alert('Please select a colour'); return false; }
    if (!state.storey)  { alert('Please select single or double storey'); return false; }
    if (!data)          { alert('Please select a gutter quality'); return false; }
    updateSizeInputs();
    updateDiagram();
    setStep(3);
    $('.ckit').attr('disabled', false);
  });

  $('.ckit').click(function() {
    $(this).attr('disabled', 'disabled');
    state.sizeA = parseInt($('input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('input[name="lengthb"]').val()) || 0;
    state.sizeC = parseInt($('input[name="lengthc"]').val()) || 0;
    if (state.sizeA < 100) {
      alert((data && data.minSizeMessage) || 'Please enter a valid width and length in millimetres. E.g. 1 metre would be 1000.');
      $(this).attr('disabled', false);
      return false;
    }
    $('.sizea span').text($('input[name="lengtha"]').val());
    $('.sizeb span').text($('input[name="lengthb"]').val());
    $('.sizec span').text($('input[name="lengthc"]').val());
    if (state.roofKey === 'three_sides_two_ext_90' || state.roofKey === 'three_sides_two_int_90' || state.roofKey === 'three_sides_one_ext_one_int_90') {
      // C is valid for 3-sided shapes — keep state.sizeC
    } else {
      state.sizeC = 0;
    }
    if (state.sizeC > 0) { $('.sizec').show(); } else { $('.sizec').hide(); }
    calculateKit();
    $(this).attr('disabled', false);
  });

  $('.srtype').click(function() {
    state.roofKey = ''; state.roofName = '';
    setStep(1);
  });

  $(".roofinput input").on('input', function() {
    $(".ckit").attr("disabled", false);
  });

  // ─── Initialise from current select state ────────────────────────────────
  // Handles cases where page inline scripts set select values before this
  // JS loads (e.g. French product page description scripts, GTM triggers).
  (function initFromCurrentState() {
    var q = $('select[name="tag2"]').val();
    if (q) {
      applyQuality(q);
      console.log('Gutter calculator: initialised data from pre-set quality select:', q);
    }
    var c = $('select[name="colour1"]:visible').val();
    if (c && c !== '') state.colour = c;
    var s = $('select[name="storey"]').val();
    if (s && s !== '') {
      state.storey = s.toLowerCase().indexOf('double') !== -1 ? 'double' : 'single';
    }
  })();

  console.log('Gutter calculator: bindings complete');

})();
